import { useId } from 'react'
import { useIntl } from 'react-intl'

import { SparklesIcon } from 'lucide-react'

import { cn } from '@/lib/utils.ts'

export interface SupportSummaryContent {
  readonly time: string
  readonly request: string
  readonly blocking: string | null
  readonly done: string
  readonly next: string
}

export interface SupportSummaryProps {
  readonly summary: SupportSummaryContent | null
  readonly pending: boolean
}

const SECTIONS = ['request', 'blocking', 'done', 'next'] as const

const SKELETON_WIDTHS = ['w-[92%]', 'w-[78%]', 'w-[85%]', 'w-[54%]']

export const SupportSummary = ({ summary, pending }: SupportSummaryProps) => {
  const intl = useIntl()
  const titleId = useId()

  if (!summary && !pending) {
    return null
  }

  const meta = summary
    ? intl.formatMessage(
        { id: pending ? 'soutien.summary.updating' : 'soutien.summary.generatedAt' },
        { time: summary.time }
      )
    : intl.formatMessage({ id: 'soutien.summary.generating' })
  const sections = summary
    ? SECTIONS.flatMap((section) => {
        const text = summary[section]
        return text ? [{ section, text }] : []
      })
    : []

  return (
    <section
      aria-labelledby={titleId}
      aria-live="polite"
      aria-busy={pending}
      className="mx-6 mt-6 mb-4 flex flex-col gap-3 rounded-[8px] border border-blue-300 bg-blue-50 px-4 pt-3.5 pb-3">
      <div className="flex flex-wrap items-center gap-2">
        <SparklesIcon aria-hidden className="size-4 text-blue-700" />
        <h3 id={titleId} className="text-base font-bold text-blue-800">
          {intl.formatMessage({ id: 'soutien.summary.title' })}
        </h3>
        <span className="text-sm text-gray-600">{meta}</span>
      </div>
      {summary ? (
        <dl
          className={cn(
            'grid grid-cols-[112px_minmax(0,1fr)] gap-x-3 gap-y-2 text-base leading-[1.4] transition-opacity duration-200',
            pending && 'opacity-50'
          )}>
          {sections.map(({ section, text }) => (
            <div key={section} className="contents">
              <dt className="font-bold text-gray-800">{intl.formatMessage({ id: `soutien.summary.${section}` })}</dt>
              <dd className="text-gray-900">{text}</dd>
            </div>
          ))}
        </dl>
      ) : (
        <div className="flex flex-col gap-2 pt-0.5 pb-1">
          <span className="text-sm text-blue-800">{intl.formatMessage({ id: 'soutien.summary.reading' })}</span>
          {SKELETON_WIDTHS.map((width) => (
            <span key={width} aria-hidden className={cn('block h-2.5 rounded-[5px] bg-blue-200', width)} />
          ))}
        </div>
      )}
      <p className="text-sm leading-[1.35] text-gray-600">{intl.formatMessage({ id: 'soutien.summary.notice' })}</p>
    </section>
  )
}
